import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useCMS } from './context/CMSContext';

// Layouts (not lazy — small, needed immediately)
import PublicLayout from './components/common/PublicLayout';
import AdminLayout from './components/admin/AdminLayout';
import ErrorBoundary from './components/common/ErrorBoundary';

// ── Lazy Public Pages ─────────────────────────────────────────────
const Home              = lazy(() => import('./pages/public/Home'));
const Mattresses        = lazy(() => import('./pages/public/Mattresses'));
const ProductPage       = lazy(() => import('./pages/public/ProductPage'));
const Pillows           = lazy(() => import('./pages/public/Pillows'));
const Duvets            = lazy(() => import('./pages/public/Duvets'));
const Toppers           = lazy(() => import('./pages/public/Toppers'));
const About             = lazy(() => import('./pages/public/About'));
const Reviews           = lazy(() => import('./pages/public/Reviews'));
const Contact           = lazy(() => import('./pages/public/Contact'));
const FAQ               = lazy(() => import('./pages/public/FAQ'));
const Delivery          = lazy(() => import('./pages/public/Delivery'));
const Quiz              = lazy(() => import('./pages/public/Quiz'));
const Register          = lazy(() => import('./pages/public/Register'));
const CustomerLogin     = lazy(() => import('./pages/public/CustomerLogin'));
const Profile           = lazy(() => import('./pages/public/Profile'));
const Cart              = lazy(() => import('./pages/public/Cart'));
const Checkout          = lazy(() => import('./pages/public/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/public/OrderConfirmation'));
const OrderHistory      = lazy(() => import('./pages/public/OrderHistory'));
const TrackOrder        = lazy(() => import('./pages/public/TrackOrder'));
const WishlistPage      = lazy(() => import('./pages/public/WishlistPage'));

// ── Lazy Admin Pages ──────────────────────────────────────────────
const AdminLogin    = lazy(() => import('./pages/admin/AdminLogin'));
const Dashboard     = lazy(() => import('./pages/admin/Dashboard'));
const ProductsAdmin = lazy(() => import('./pages/admin/ProductsAdmin'));
const ProductEditor = lazy(() => import('./pages/admin/ProductEditor'));
const BannersAdmin  = lazy(() => import('./pages/admin/BannersAdmin'));
const ThemesAdmin   = lazy(() => import('./pages/admin/ThemesAdmin'));
const SettingsAdmin = lazy(() => import('./pages/admin/SettingsAdmin'));
const OrdersAdmin   = lazy(() => import('./pages/admin/OrdersAdmin'));
const OrderDetail   = lazy(() => import('./pages/admin/OrderDetail'));
const DiscountsAdmin = lazy(() => import('./pages/admin/DiscountsAdmin'));
const DeliveryAdmin  = lazy(() => import('./pages/admin/DeliveryAdmin'));
const ReviewsAdmin    = lazy(() => import('./pages/admin/ReviewsAdmin'));
const ContactAdmin    = lazy(() => import('./pages/admin/ContactAdmin'));
const UsersAdmin      = lazy(() => import('./pages/admin/UsersAdmin'));
const CategoriesAdmin = lazy(() => import('./pages/admin/CategoriesAdmin'));
const PressAdmin      = lazy(() => import('./pages/admin/PressAdmin'));
const PagesAdmin      = lazy(() => import('./pages/admin/PagesAdmin'));
const FooterAdmin     = lazy(() => import('./pages/admin/FooterAdmin'));

// ── Page-level loading fallback ───────────────────────────────────
const PageLoader = () => (
  <div
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        border: '3px solid var(--gray-200)',
        borderTopColor: 'var(--teal)',
      }}
      className="animate-spin"
    />
  </div>
);

// ── Protected Route ───────────────────────────────────────────────
const AdminOnlyRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) return <PageLoader />;

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AdminLoginRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) return <PageLoader />;

  if (isAuthenticated && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (isAuthenticated && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// ── Google Analytics (gtag.js) — injected when a Measurement ID is configured ──
const GoogleAnalytics = () => {
  const { settings } = useCMS();
  const gaId = settings?.gaMeasurementId;

  useEffect(() => {
    if (!gaId || /^G-XXX/i.test(gaId)) return;
    if (document.getElementById('ga-gtag-src')) return;

    const script = document.createElement('script');
    script.id = 'ga-gtag-src';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
    document.head.appendChild(script);

    const inline = document.createElement('script');
    inline.id = 'ga-gtag-init';
    inline.innerHTML = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${gaId}');`;
    document.head.appendChild(inline);
  }, [gaId]);

  return null;
};

// ── App ───────────────────────────────────────────────────────────
function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <GoogleAnalytics />
        <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/"                       element={<Home />} />
          <Route path="/mattresses"             element={<Mattresses />} />
          <Route path="/mattresses/:slug"       element={<ProductPage />} />
          <Route path="/pillows"                element={<Pillows />} />
          <Route path="/duvets"                 element={<Duvets />} />
          <Route path="/toppers"                element={<Toppers />} />
          <Route path="/about"                  element={<About />} />
          <Route path="/reviews"                element={<Reviews />} />
          <Route path="/contact"                element={<Contact />} />
          <Route path="/faq"                    element={<FAQ />} />
          <Route path="/delivery"               element={<Delivery />} />
          <Route path="/quiz"                   element={<Quiz />} />
          <Route path="/register"               element={<Register />} />
          <Route path="/login"                  element={<CustomerLogin />} />
          <Route path="/profile"                element={<Profile />} />
          <Route path="/cart"                   element={<Cart />} />
          <Route path="/checkout"               element={<Checkout />} />
          <Route path="/order/:id/confirmation" element={<OrderConfirmation />} />
          <Route path="/orders"                 element={<OrderHistory />} />
          <Route path="/track-order"            element={<TrackOrder />} />
          <Route path="/wishlist"               element={<WishlistPage />} />
        </Route>

        {/* Admin Login (no layout) */}
        <Route path="/admin/login" element={
          <AdminLoginRoute>
            <AdminLogin />
          </AdminLoginRoute>
        } />

        {/* Protected Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminOnlyRoute>
              <AdminLayout />
            </AdminOnlyRoute>
          }
        >
          <Route index                      element={<Dashboard />} />
          <Route path="products"            element={<ProductsAdmin />} />
          <Route path="products/new"        element={<ProductEditor />} />
          <Route path="products/:id"        element={<ProductEditor />} />
          <Route path="banners"             element={<BannersAdmin />} />
          <Route path="themes"              element={<ThemesAdmin />} />
          <Route path="settings"            element={<SettingsAdmin />} />
          <Route path="orders"              element={<OrdersAdmin />} />
          <Route path="orders/:id"          element={<OrderDetail />} />
          <Route path="discounts"           element={<DiscountsAdmin />} />
          <Route path="delivery"            element={<DeliveryAdmin />} />
          <Route path="categories"          element={<CategoriesAdmin />} />
          <Route path="reviews"             element={<ReviewsAdmin />} />
          <Route path="contact"            element={<ContactAdmin />} />
          <Route path="users"               element={<UsersAdmin />} />
          <Route path="press"               element={<PressAdmin />} />
          <Route path="pages"               element={<PagesAdmin />} />
          <Route path="footer"              element={<FooterAdmin />} />
        </Route>

        {/* 404 Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
